import { DRAG_NODE_TO_PAN, FREEHAND_PADDING } from 'lib/constants';
import { zoomUI } from 'lib/zoom-ui';
import { appUI } from 'lib/app-ui';
import { scaleRect } from 'lib/util';
import { computeSlope, normalizeAngle, pathRectIntersection } from 'lib/geom';
import uploadHandler from 'lib/node-upload';

class NodesUI {

  constructor() {
    this.nodeTopLineZoomClasses = {
      level_1: 'node-top-line-level-1',
      top_level: 'node-top-line-top-level',
    };
    this.rects = {};
    this.expandMoveIds = {};
    this.expandMoves = {};
    this.gutterSize = 24;
    this.uploadHandler = uploadHandler;
    this.clippedLinkTags = {};
  }

  setCanvas(canvas) {
    this.canvas = canvas;
    this.operation = canvas.operation;
    this.lastMouseMove = {x: 0, y: 0};
  }

  setMovingParts(nodes) {
    this.movingParts = nodes;
  }

  getMovingParts() {
    return this.movingParts || [];
  }

  dontAllowDropInTarget (draggingNode, targetNode) {

    if (draggingNode && targetNode) {
      if (draggingNode.link()) return true;

      const disallowedElements = this.getMovingParts();
      if (disallowedElements && disallowedElements.targetNodeIds && disallowedElements.targetNodeIds.includes(targetNode.id)) return true;
    }
    return false;
  };

  maybePan(nodeTag) {
    if (!nodeTag.isDragSubject() || !this.canvas.checkSelectionDragLegality()) {
      return;
    }

    const nodes = this.canvas.getSelectedNodes().length
      ? this.canvas.getSelectedNodes()
      : [nodeTag.instance];
    const move = this._calculateMove(nodes);

    if (move.el.x == 0 && move.el.y == 0) {
      window.requestAnimationFrame(this.maybePan.bind(this, nodeTag));
      return;
    }

    if (this.canvas.panZoomDisabled()) {
      this.canvas.enablePanZoom();
      $('#canvas').panzoom("pan", move.pan.x, move.pan.y, {relative: true});
      this.canvas.disablePanZoom();
    } else {
      $('#canvas').panzoom("pan", move.pan.x, move.pan.y, {relative: true});
    }

    if (this.canvas.getSelectedNodes().length) {
      for (const node of this.canvas.getSelectedNodes()) {
        const nt = this.canvas.nodeTags[node.id];
        if (!nt) continue;
        if (nt.emancipating || !nt.getParent()) {
          nt.instance.applyMove({ dx: move.el.x, dy: move.el.y, appliedMoveLookup: {} });
          nt.updateNodesDisplayRecursively();
        }
      }
    } else {
      nodeTag.instance.applyMove({ dx: move.el.x, dy: move.el.y, appliedMoveLookup: {} });
      nodeTag.updateNodesDisplayRecursively();
    }
    window.requestAnimationFrame(this.maybePan.bind(this, nodeTag));
  }

  _calculateMove(nodes) {
    const maxMove = DRAG_NODE_TO_PAN.MAX_MOVE;
    let bumper = DRAG_NODE_TO_PAN.BUMPER;
    const move = {
      el: {
        x: 0,
        y: 0,
      },
      pan: {
        x: 0,
        y: 0,
      },
      dirX: '',
      dirY: '',
    };

    const scale = this.canvas.getMatrix().scale;

    for (const node of nodes) {
      const nodeTag = this.canvas.nodeTags[node.id];

      if (nodeTag) {
        const rect = nodeTag._draggingRect;

        if (!rect || !rect.width) continue;

        // if it's a tall or wide card, move the bumper outside of the
        // viewport.
        if (rect.height > (window.innerHeight - (bumper * 4))) {
          bumper = bumper * -2;
        }
        if (rect.width > (window.innerWidth - (bumper * 4))) {
          bumper = bumper * -2;
        }

        if ((rect.x <= bumper) && (this.canvas.dragDirectionX == 'left')) {
          move.el.x = (maxMove / scale) * -1;
          move.pan.x = (maxMove * 1);
          move.dirX = 'left';
        }
        if ((rect.right >= window.innerWidth - bumper) && (this.canvas.dragDirectionX == 'right')) {
          move.el.x = (maxMove / scale) * 1;
          move.pan.x = (maxMove * -1);
          move.dirX = 'right';
        }
        if ((rect.y <= bumper) && (this.canvas.dragDirectionY == 'up')) {
          move.el.y = (maxMove / scale) * -1;
          move.pan.y = (maxMove * 1);
          move.dirY = 'up';
        }
        if ((rect.bottom >= window.innerHeight - bumper) && (this.canvas.dragDirectionY == 'down')) {
          move.el.y = (maxMove / scale) * 1;
          move.pan.y = (maxMove * -1);
          move.dirY = 'down';
        }
      }
    }

    return move;
  }

  computeMovingParts(store, instance) {
    const seenNodes = {};
    const seenLinks = {};
    const targetNodeIds = [];
    const targetLinkIds = [];
    const getIds = startingNode => {
      if (seenNodes[ startingNode.id ]) {
        return;
      }
      seenNodes[ startingNode.id ] = 1;
      targetNodeIds.push(startingNode.id);
      if (store.getNode(startingNode.id).link()) {
        const link = store.getNode(startingNode.id).link();
        if (!seenLinks[link.id]) {
          seenLinks[link.id] = 1;
          targetLinkIds.push(link.id);
        }
      }
      for (let link of startingNode.descendantsLinks()) {
        if (!seenLinks[link.id]) {
          seenLinks[link.id] = 1;
          targetLinkIds.push(link.id);
          if (link.linkNode()) {
            getIds(link.linkNode());
          }
        }
      }
      for (let child of startingNode.children()) {
        getIds(child);
      }
    };
    getIds(instance);

    return { targetNodeIds, targetLinkIds };

  }

  computeAllSelectedMovingParts(store,instance,canvas) {
    const targets = this.computeMovingParts(store,instance);
    let targetNodeIds = targets.targetNodeIds;
    let targetLinkIds = targets.targetLinkIds;

    const selected = canvas.getSelectedElements();
    if (selected.nodes.length + selected.links.length > 1) {
      for (let node of selected.nodes) {
        if (node.id != instance.id) {
          const descendants = this.computeMovingParts(store,node);
          targetNodeIds = targetNodeIds.concat(descendants.targetNodeIds);
          targetLinkIds = targetLinkIds.concat(descendants.targetLinkIds);
        }
      }

      for (let link of selected.links) {
        if (link.linkNodeId && link.linkNode()) {
          const descendants = this.computeMovingParts(store,link.linkNode());
          targetNodeIds = targetNodeIds.concat(descendants.targetNodeIds);
          targetLinkIds = targetLinkIds.concat(descendants.targetLinkIds);
        }
      }
    }

    const seenNodeIds = {};
    const seenLinkIds = {};
    const uniqTargetNodeIds = targetNodeIds.filter(id => {
      if (!seenNodeIds[id]) {
        seenNodeIds[id] = 1;
        return true;
      }
      return false;
    });
    const uniqTargetLinkIds = targetLinkIds.filter(id => {
      if (!seenLinkIds[id]) {
        seenLinkIds[id] = 1;
        return true;
      }
      return false;
    });
    return { targetNodeIds: uniqTargetNodeIds, targetLinkIds: uniqTargetLinkIds };
  }

  deselectParentsOf(instance) {
    const ancestors = instance.ancestors();
    const {nodes} = this.canvas.getSelectedElements();
    const selectedAncestors = ancestors.filter(a => nodes.find(n => n.id == a.id));
    for (const node of selectedAncestors) {
      this.canvas.clearSelectedElement( this.canvas.nodeTags[node.id] );
    }
  }

  deselectChildrenOf(instance) {
    const descendants = instance.descendants();
    const {nodes} = this.canvas.getSelectedElements();
    const selectedDescendants = descendants.filter(a => nodes.find(n => n.id == a.id));
    for (const node of selectedDescendants) {
      this.canvas.clearSelectedElement( this.canvas.nodeTags[node.id] );
    }
  }

  // return true if nodeA should appear instead of B if they overlap
  zoomPrecendenceOver(nodeA,nodeB,zoomLevel) {
    // if one of the nodes doesn't exist it goes on bottom
    if (!nodeA) {
      return false;
    } else if (!nodeB) {
      return true;
    }

    // if another node was hidden at a lower zoom level, then this takes precedence
    if (nodeB.getZoomLevelHidden() && nodeB.getZoomLevelHidden() >= zoomLevel) {
      return true;
    }
    // nodes with titles take precendence
    if (nodeA.title && !nodeB.title) {
      return true;
    }
    if (!nodeA.title && nodeB.title) {
      return false;
    }
    // top level nodes take precedence
    if (!nodeA.parentId && !nodeA.link() && nodeB.parentId) {
      return true;
    }
    if (nodeA.parentId && !nodeB.parentId && !nodeB.link()) {
      return false;
    }
    // non-link nodes take precedence
    if (!nodeA.link() && nodeB.link()) {
      return true;
    }
    if (nodeA.link() && !nodeB.link()) {
      return false;
    }
    // the shorter title wins
    if (nodeA.title.length < nodeB.title.length) {
      return true;
    }
    if (nodeA.title.length > nodeB.title.length) {
      return false;
    }
    if (nodeA.absolutePosition.y < nodeB.absolutePosition.y) {
      return true;
    }
    return false;
  }

  // give cards
  getNodeTopLineZoomClass(nodeTag) {
    let className = '';
    const node = this.canvas.store.getNode(nodeTag.instance.id);
    if (node && node.parentId) {
      if (node.getParent() && !node.getParent().parentId) {
        className = this.nodeTopLineZoomClasses.level_1;
      }
    } else {
      className = this.nodeTopLineZoomClasses.top_level;
    }
    return className;
  }

  updateNodeTopLineZoomClass(nodeTag){
    const className = this.getNodeTopLineZoomClass(nodeTag);
    const classes = Object.values(this.nodeTopLineZoomClasses);

    if (className) {
      nodeTag.refs.nodeTopLine.classList.add(className);
    }
    for (const c of classes) {
      if (c != className) {
        nodeTag.refs.nodeTopLine.classList.remove(c);
      }
    }
  }

  ungroupSystems(nodeIds) {
    if (!nodeIds || !nodeIds.length ) {
      return;
    }
    const nodes = nodeIds.map(id => this.canvas.store.getNode(id));
    const operationMessages = [];
    for (const id of nodeIds) {
      const message = this.ungroupSystem(id);
      operationMessages.push(message);
    }
    this.canvas.operation.batchOperation({
      messages: operationMessages,
      onFailure: (err) => {
        console.log(`ungroupSystems batch failure: ${err}`);
      }
    });
  }

  // pull all children out of a parent and retain their relative positions
  ungroupSystem(parentId) {
    const parent = this.canvas.store.getNode(parentId);
    const children = parent.children();
    const prevNodePositions = this.canvas.store.getNodePositions(children.map(c => c.id));
    const prevLinkAngles = store.getLinkAngles(parent.links().map(l => l.id));
    const selectedNodes = [parentId];

    if (parent.parentId) {
      // pop children into the parent one level up
      const siblings = parent.getParent().children();
      const lastOrderIndex = siblings[siblings.length - 1].orderIndex;
      for (const child of children) {
        child.emancipateSelf({
          x: (child.absolutePosition.x - parent.absolutePosition.x) + parent.x,
          y: (child.absolutePosition.y - parent.absolutePosition.y) + parent.y,
        });
        child.adoptParent(parent.parentId);
        child.setOrderIndex(child.orderIndex + lastOrderIndex);
        selectedNodes.push(child.id);
      }

    } else {
      // or pop them into the top level
      for (const child of children) {
        child.emancipateSelf({
          x: child.absolutePosition.x,
          y: child.absolutePosition.y
        });
        selectedNodes.push(child.id);
      }
    }
    const nodePositions = this.canvas.store.getNodePositions(children.map(c => c.id));
    const linkAngles = store.getLinkAngles(parent.links().map(l => l.id));
    canvas.nodesTag.update();
    canvas.nodeTags[parentId].update();
    canvas.nodeTags[parentId].rediscoverLinkPositions();
    const message = this.canvas.operation.messages.updateNodePositions({
      prevNodePositions,
      prevLinkAngles,
      nodePositions,
      linkAngles,
      selectedNodes,
    });
    return message;
  }


  _groupNodeByParent(nodes) {
    const nodeGroupsByParent = {};
    for (const node of nodes) {
      if (node.parentId) {
        if (!nodeGroupsByParent[node.parentId]) {
          nodeGroupsByParent[node.parentId] = [];
        }
        nodeGroupsByParent[node.parentId].push(node);
      }
    }
    return nodeGroupsByParent;
  }

  _getAllLinkIdsForNodes(nodes) {
    let allAffectedLinks = [];
    for (const node of nodes) {
      allAffectedLinks = allAffectedLinks.concat(node.links().map(l => l.id));
    }
    return allAffectedLinks;
  }

  bulkEmancipateLinkNodes(linkNodes,operationMessages) {
    const linksToUpdate = [];
    for (const node of linkNodes) {
      const link = node.link();
      if (!link) {
        continue;
      }
      const prevLink = Object.assign({}, link);
      link.setLinkNodeId(null);
      const setLinkNodeIdMessage = this.operation.messages.setLinkNodeId({
        link,
        prevLink
      });
      operationMessages.push(setLinkNodeIdMessage);
      linksToUpdate.push(link);
    }

    for (const linkNode of linkNodes) {
      if (canvas.nodeTags[linkNode.id]) {
        this.canvas.nodeTags[linkNode.id].updateLayers();
      }
    }
    for (const link of linksToUpdate) {
      if (this.canvas.linkTags[link.id]) {
        this.canvas.linkTags[link.id].removeClipPolygon();
      }
    }
  }

  _getEmancipationDirection({ childExtents, parentExtents}) {
    const [et, el, eb, er] = childExtents;
    const [parentET, parentEL, parentEB, parentER] = parentExtents;
    const height = eb - et;
    const width = er - el;
    const topDist = et - parentET;
    const bottomDist = parentEB - eb;
    const leftDist = el - parentEL;
    const rightDist = parentER - er;
    const smallest = Math.min.apply(null,[topDist,bottomDist,leftDist,rightDist]);
    const BUFFER = 80; // arbitrary space between new position and old parent

    if (smallest == topDist) {
      return { direction: 'top', y: (topDist + height) * -1 - BUFFER, x: 0 };
    } else if (smallest == rightDist) {
      return { direction: 'right', y: 0, x: rightDist + width + BUFFER };
    } else if (smallest == bottomDist) {
      return { direction: 'bottom', y: bottomDist + height + BUFFER, x: 0 };
    } else {
      return { direction: 'left', y: 0, x: (leftDist + width) * -1 - BUFFER};
    }
  }

  // emancipate a group of children and position them outside the parent container
  _emancipateChildGroup(children) {
    const parent = this.canvas.store.getNode(children[0].parentId);
    let maxYDiff = 0;
    let maxXDiff = 0;

    // if we're not freeing all children, then we'll need to offset their new positions outside the parent card
    // otherwise we can keep them where they are.
    let delta = { x: 0, y: 0 };
    const emancipatedChildren = [];
    if (parent.children().length == children.length) {

      for (const child of children) {
        if (child.parentId) {
          child.emancipateSelf({
            x: child.absolutePosition.x,
            y: child.absolutePosition.y
          });
          emancipatedChildren.push(child);
        }
      }

      this.canvas.nodeTags[parent.id].update();
      this.canvas.childNodeTags[parent.id].update();
    } else {
      for (const child of children) {
        if (!child.absolutePosition || !child.absolutePosition.x) {
          this.canvas.nodeTags[child.id].discoverPosition();
        }
      }

      const childExtents = this.canvas.getContentExtents({nodes: children});
      // size the parent container down so that we can place the child nodes next to its footprint after emancipation

      for (const child of children) {
        if (child.parentId) {
          child.emancipateSelf({
            x: child.absolutePosition.x,
            y: child.absolutePosition.y
          });
          emancipatedChildren.push(child);
        }
      }

      let hidChildren = false;
      if (parent.getLayoutName() == 'layout-freehand') {
        this.canvas.nodeTags[parent.id].update();
        this.canvas.childNodeTags[parent.id].update();
      } else {
        for (const child of children) {
          this.canvas.nodeTags[child.id].refs.main.style.display = 'none';
        }
        hidChildren = true;
      }
      const parentExtents = this.canvas.getContentExtents({nodes: [parent]});
      if (hidChildren) {
        for (const child of children) {
          this.canvas.nodeTags[child.id].refs.main.style.display = '';
        }
      }
      delta = this._getEmancipationDirection({
        childExtents,
        parentExtents
      });
    }

    for (const child of emancipatedChildren) {
      child.setAbsolutePosition({
        x: child.absolutePosition.x + delta.x,
        y: child.absolutePosition.y + delta.y
      });
      child.setPosition({
        x: child.absolutePosition.x,
        y: child.absolutePosition.y
      });
    }

    return emancipatedChildren;
  }

  detachParts(nodeIds) {
    if (!nodeIds || !nodeIds.length ) {
      return;
    }
    const nodes = nodeIds.map(id => this.canvas.store.getNode(id));
    const operationMessages = [];
    this.bulkEmancipateParts(nodes,operationMessages);
    this.canvas.operation.batchOperation({
      messages: operationMessages,
      onFailure: (err) => {
        console.log(`detachParts batch failure: ${err}`);
      }
    });
  }

  bulkEmancipateParts(nodes,operationMessages) {
    // if top level nodes with children are selected, include their direct children

    const allAffectedNodes = nodes.concat([]);
    const nodeGroupsByParent = this._groupNodeByParent(nodes);
    const parentNodes = [];
    for (const parentId in nodeGroupsByParent) {
      const parent = this.canvas.store.getNode(parentId);
      parentNodes.push(parent);
      allAffectedNodes.push(parent);
    }

    const allAffectedLinks = this._getAllLinkIdsForNodes(allAffectedNodes);
    const prevNodePositions = this.canvas.store.getNodePositions(allAffectedNodes.map(c => c.id));
    const prevLinkAngles = this.canvas.store.getLinkAngles(allAffectedLinks);


    for (const parentId in nodeGroupsByParent) {
      // remove selected cards nested inside other selected cards
      if (nodes.find(n => n.id == parentId)) {

        if (this.canvas.store.getNode(parentId).parentId) {
          delete nodeGroupsByParent[parentId];
        }
      }
    }
    let emancipatedChildren = [];
    for (const parentId in nodeGroupsByParent) {
      const children = nodeGroupsByParent[parentId];
      const _emancipatedChildren = this._emancipateChildGroup(children);
      emancipatedChildren = emancipatedChildren.concat(_emancipatedChildren);
    }

    const nodePositions = this.canvas.store.getNodePositions(allAffectedNodes.map(c => c.id));
    const linkAngles = this.canvas.store.getLinkAngles(allAffectedLinks);
    canvas.nodesTag.update();
    for (const node of allAffectedNodes) {
      if (this.canvas.nodeTags[node.id]) {
        this.canvas.nodeTags[node.id].update({ forceUpdate: true });

        if (node.children().length && node.getLayoutName() == 'layout-freehand') {
          this.canvas.childNodeTags[node.id].fitToContents();
        }
        this.canvas.nodeTags[node.id].rediscoverLinkPositions();
      }
    }

    // if most nodes are out of the viewport, center in view
    if (!appUI.areNodesMostlyInView(emancipatedChildren)) {
      this.canvas.centerContent({ nodes: emancipatedChildren }, { scale: this.canvas.getMatrix().scale });
    }

    const updateNodePositionsMessage = this.canvas.operation.messages.updateNodePositions({
      prevNodePositions,
      prevLinkAngles,
      nodePositions,
      linkAngles,
      selectedNodes: allAffectedNodes.map(n => { const {_store, _cache, ...newNode} = n; return newNode;}),
    });
    operationMessages.push(updateNodePositionsMessage);


    canvas.operation.batchOperation({
      messages: operationMessages,
      onFailure: (err) => {
        console.log(`detachParts batch failure: ${err}`);
      }
    });
  }

  // given a list of nodes, filter out nodes that are children of other members of the list
  getHighestLevelNodesInList(nodes) {
    return nodes.filter(n => {
      if (!n.parentId) return true;
      // any node who's parent is also in nodes gets filtered out
      if (nodes.find(_n => _n.id == n.parentId)) return false;

      return true;
    });
  }

  detachRelationshipCard(nodeId) {
    const node = store.getNode(nodeId);
    if (!node) {
      return;
    }
    const link = node.link();
    if (!link) {
      return;
    }
    const prevLink = Object.assign({}, link);
    link.setLinkNodeId(null);
    canvas.operation.setLinkNodeId({
      link,
      prevLink,
      onFailure: err => console.log(err),
    });
    if (canvas.nodeTags[node.id]) {
      canvas.nodeTags[node.id].updateTopLevelNeighborsLite();
    }
    if (canvas.linkTags[link.id]) {
      canvas.linkTags[link.id].removeClipPolygon();
    }
  }

  detachPart(nodeId) {
    const node = store.getNode(nodeId);
    if (!node) {
      return;
    }
    const nodeTag = canvas.nodeTags[node.id];
    if (!nodeTag) {
      return;
    }
    const prevNodePositions = this.canvas.store.getNodePositions([node.id]);
    const { scale, tx, ty } = this.canvas.getMatrix();

    const topParent = node.getTopLevelParent();
    let nodeRect = nodeTag.refs.container.getBoundingClientRect();
    let topParentRect = this.canvas.nodeTags[topParent.id].refs.container.getBoundingClientRect();
    let closestEdge = (((nodeRect.left + nodeRect.right) / 2) < ((topParentRect.left + topParentRect.right) / 2)) ? 'left' : 'right';

    if (node.getGoverningLayoutName() !== 'layout-freehand') closestEdge = 'left';
    const oldParentId = node.parentId;
    const oldParent = this.canvas.store.getNode(oldParentId);

    const formerParent = this.canvas.nodeTags[node.parentId];

    node.setOrderIndex(0);
    node.setParentId(null);
    nodeRect = nodeTag.refs.container.getBoundingClientRect();

    canvas.nodeTags[oldParentId].updateTopLevelNeighborsLite();
    nodeTag.updateLayers();
    canvas.nodesTag.update();

    topParentRect = this.canvas.nodeTags[topParent.id].refs.container.getBoundingClientRect();

    let newXPos;
    if (closestEdge === 'left') {
      newXPos = (topParentRect.left - nodeRect.width - 20);
    } else {
      newXPos = (topParentRect.right + 20);
    }

    node.setAbsolutePosition({
      x: (newXPos - tx) / scale,
      y: node.absolutePosition.y,
    });
    node.setPosition({
      x: (newXPos - tx) / scale,
      y: node.absolutePosition.y,
    });

    this.canvas.nodeTags[node.id].updateTopLevelNeighborsLite();
    const operationMessages = [];
    operationMessages.push(
      this.operation.messages.updateNodePositions({
        prevNodePositions,
        nodePositions: store.getNodePositions([node.id]),
        prevLinkAngles: [],
        linkAngles: [],
        selectedNodes: [node.id,formerParent.id],
      })
    );

    if (oldParent.getLayoutName() === 'layout-freehand') {
      if (oldParent.children().length < 1) {

        oldParent.setLayoutName('layout-row');

        operationMessages.push(
          this.operation.messages.setNodeLayoutName({
            nodeUUID: oldParentId,
            prevLayoutName: 'layout-freehand',
            layoutName: 'layout-row',
          })
        );
      }
    }

    this.operation.batchOperation({
      messages: operationMessages,
      onFailure: err => {
        console.warn(`emancipatePart batchOperation failed: ${err}`);
      }
    });

    nodeTag.child = false;
  }


  getBulkCardStyle(nodeIds) {
    const styles = [];
    const seen = {};
    for (const id of nodeIds) {
      const node = this.canvas.store.getNode(id);
      if (node && node.getDisplayStyle() && !seen[node.getDisplayStyle()]) {
        styles.push(node.getDisplayStyle());
        seen[node.getDisplayStyle()] = true;
      }
    }
    if (styles.length == 1) {
      return styles[0];
    }
    return null;
  }

  getBulkCardColor(nodeIds) {
    const colors = [];
    const seen = {};
    for (const id of nodeIds) {
      const node = this.canvas.store.getNode(id);
      if (node && !seen[node.getBackgroundColor()]) {
        colors.push(node.getBackgroundColor());
        seen[node.getBackgroundColor()] = true;
      }
    }
    if (colors.length == 1) {
      return colors[0];
    }
    return null;
  }


  setCardDisplayStyleAndColor({color, nodeIds, style}) {
    const messages = [];

    for (const id of nodeIds) {
      const node = this.canvas.store.getNode(id);
      const prevStyle = node.getDisplayStyle();
      const newStyle = style;
      const prevColor = node.getBackgroundColor();
      const newColor = color;
      node.setBackgroundColor(color);
      node.setDisplayStyle(style);
      this.canvas.nodeTags[node.id].updateDescendants();
      messages.push(this.canvas.operation.messages.setNodeBackgroundColor({
        nodeUUID: node.id,
        prevColor,
        color: newColor
      }));
      messages.push(this.canvas.operation.messages.setNodeDisplayStyle({
        nodeUUID: node.id,
        prevStyle,
        style: newStyle
      }));
    }

    if (messages.length) {
      this.canvas.operation.batchOperation({
        messages,
        onFailure: (err) => {
          console.log(`setDisplayStyleAndColor batch failure: ${err}`);
        }
      });
    }
  }

  setCardColor(color,nodeIds) {
    const messages = [];
    for (const id of nodeIds) {
      const node = this.canvas.store.getNode(id);
      const prevColor = node.getBackgroundColor();
      const newColor = color;
      node.setBackgroundColor(color);
      this.canvas.nodeTags[node.id].updateDescendants();
      messages.push(this.canvas.operation.messages.setNodeBackgroundColor({
        nodeUUID: node.id,
        prevColor,
        color: newColor
      }));
    }

    if (messages.length) {
      this.canvas.operation.batchOperation({
        messages,
        onFailure: (err) => {
          console.log(`setColor batch failure: ${err}`);
        }
      });
    }
  }

  setDisplayStyle(style,nodeIds) {
    const messages = [];
    for (const id of nodeIds) {
      const node = this.canvas.store.getNode(id);
      const prevStyle = node.getDisplayStyle();
      const newStyle = style;
      node.setDisplayStyle(style);
      this.canvas.nodeTags[node.id].updateDescendants();
      messages.push(this.canvas.operation.messages.setNodeDisplayStyle({
        nodeUUID: node.id,
        prevStyle,
        style: newStyle
      }));
    }

    if (messages.length) {
      this.canvas.operation.batchOperation({
        messages,
        onFailure: (err) => {
          console.log(`setDisplayStyle batch failure: ${err}`);
        }
      });
    }
  }

  hasFreehandDescendant(nodeTag) {
    if (!this.checkTag(nodeTag)) {
      return;
    }
    if (nodeTag.instance.getLayoutName() === 'layout-freehand') {
      return true;
    }
    if (nodeTag.instance.descendants().find( descendant => { return descendant.getLayoutName() === 'layout-freehand'; })) {
      return true;
    }
  }

  checkTag(tag) {
    if (!(tag && tag.instance && tag.instance.id && tag.refs && tag.refs.container)) {
      console.trace(`bad argument to checkTag: ${tag}`);
      return false;
    }
    return true;
  }

  calculateTagRects() {
    for (const topNode of this.canvas.store.topNodes()) {
      if (topNode.getHidden()) {
        continue;
      }
      const nodeTag = this.canvas.nodeTags[topNode.id];
      if (!this.checkTag(nodeTag)) {
        continue;
      }

      this.calculateTagRect(nodeTag);

      if (this.hasFreehandDescendant(nodeTag)) {
        this.calculateFreehandDescendantRects(nodeTag);
      }
    }
  }

  calculateFreehandDescendantRects(nodeTag) {
    if (!this.checkTag(nodeTag)) {
      return;
    }

    if (nodeTag.instance.getLayoutName() === 'layout-freehand') {
      this.calculateTagRect(nodeTag);

      nodeTag.instance.children().forEach(child => {
        const childTag = this.canvas.nodeTags[child.id];
        if (!this.checkTag(childTag)) {
          return;
        }

        this.calculateTagRect(childTag);

        if (this.hasFreehandDescendant(childTag)) {
          this.calculateFreehandDescendantRects(childTag);
        }
      });
    } else {
      nodeTag.instance.children().forEach(child => {
        const childTag = this.canvas.nodeTags[child.id];
        if (this.checkTag(childTag)) {
          this.calculateFreehandDescendantRects(childTag);
        }
      });
    }
  }

  getFirstFreehandAncestorId(nodeTag) {
    if (!this.checkTag(nodeTag)) {
      return;
    }

    const ancestors = nodeTag.instance.ancestors();
    const idx = ancestors.findIndex(a => { return a.getLayoutName() == 'layout-freehand'; });
    if (idx === -1) {
      return;
    }

    return ancestors[idx].id;
  }

  calculateTagRect(nodeTag) {
    if (!this.checkTag(nodeTag)) {
      return;
    }

    const rect = nodeTag.refs.container.getBoundingClientRect();
    if (!(rect && rect.width)) {
      return;
    }

    this.rects[nodeTag.instance.id] = rect;
  }

  getRect(id) {
    if (!id) {
      throw new Error(`id is required`);
    }
    return this.rects[id];
  }

  hit(rectA, rectB, b, scale) {

    b *= scale;
    console.log("B", b, scale);

    // why are we adding and subtracting scale?  scale is equal to one pixel scaled...
    return !(rectA.right + b - scale*2 < rectB.left || rectA.left - b + scale*2 > rectB.right ||
            rectA.bottom + b - scale*2 < rectB.top || rectA.top - b + scale*2 > rectB.bottom);
  }

  trackExpandMoves({ instigator, nodeMoves }) {
    if (!(nodeMoves && Object.keys(nodeMoves).length)) {
      return;
    }
    this.expandMoves[instigator.id] = nodeMoves;
    Object.keys(nodeMoves).forEach(id => {
      this.expandMoveIds[id] = instigator.id;
    });
  }

  invalidateExpandMove(nodeTag) {
    if (!this.checkTag(nodeTag)) {
      return;
    }
    const movedId = nodeTag.instance.id;
    const instigatorId = this.expandMoveIds[movedId];
    delete this.expandMoveIds[movedId];
    if (instigatorId) {
      delete this.expandMoves[instigatorId][movedId];
    }
  }

  applyInvertedExpandMoves(nodeTag) {
    if (!this.checkTag(nodeTag)) {
      return;
    }

    const collapsedId = nodeTag.instance.id;
    if (!this.expandMoves[collapsedId]) {
      return { prevNodePositions: [], nodePositions: [] };
    }

    const expandMoves = this.expandMoves[collapsedId];
    const nodeIds = Object.keys(expandMoves);
    const prevNodePositions = this.canvas.store.getNodePositions(nodeIds);

    Object.keys(expandMoves).forEach(id => {
      const move = expandMoves[id].move;
      const tag = this.canvas.nodeTags[id];
      if (tag) {
        tag.instance.applyMove({ dx: move.dx * -1, dy: move.dy * -1, appliedMoveLookup: {} });
        tag.updateNodesDisplayRecursively();
      }
      this.invalidateExpandMove(tag);
    });

    const nodePositions = this.canvas.store.getNodePositions(nodeIds);
    return { prevNodePositions, nodePositions };
  }

  /*
    allowedDirections: ['right','left','up','down']
  */
  fixOverlaps({ nodeTag, fromExpand, fromCollapse, allowedDirections }) {
    if (!this.checkTag(nodeTag)) {
      return;
    }

    this.calculateTagRects();

    if (!(fromExpand || fromCollapse)) {
      this.invalidateExpandMove(nodeTag);
    }

    if (fromCollapse) {
      const { prevNodePositions, nodePositions } = this.applyInvertedExpandMoves(nodeTag);
      return { prevNodePositions, nodePositions };
    }

    // filled by fixOverlapsRecursively so we can roll our updateNodePositions
    // operation into the caller.
    const collectedData = {};

    const instigatorTag = this.getInstigatorTag(nodeTag);
    this.fixOverlapsRecursively({ instigatorTag, collectedData, allowedDirections });

    if (fromExpand) {
      this.trackExpandMoves({ instigator: nodeTag.instance, nodeMoves: collectedData.nodeMoves });
    }

    if (!collectedData.prevNodePositions) {
      return { prevNodePositions: [], nodePositions: [] };
    }

    const movedIds = Object.keys(collectedData.prevNodePositions);
    if (!movedIds.length) {
      return { prevNodePositions: [], nodePositions: [] };
    }

    const prevNodePositions = Object.values(collectedData.prevNodePositions);
    const nodePositions = this.canvas.store.getNodePositions(movedIds);

    return { prevNodePositions, nodePositions };
  }

  fixOverlapsRecursively({ instigatorTag, prevDirection, collectedData, allowedDirections }) {
    if (!(this.checkTag(instigatorTag) && collectedData)) {
      return;
    }

    const rect = this.getRect(instigatorTag.instance.id);
    if (!rect) {
      return;
    }

    if (!collectedData.nodeMoves) {
      collectedData.nodeMoves = {};
    }
    if (!collectedData.prevNodePositions) {
      collectedData.prevNodePositions= {};
    }

    let targetIds = [];

    const instigatorParent = instigatorTag.instance.getParent();
    const isFreehandChild = instigatorParent && instigatorParent.getLayoutName() === 'layout-freehand';
    const instigatorIsRnode = Boolean(instigatorTag.link());

    // if we're a child of a freehand, we can only move siblings in that freehand
    if (isFreehandChild) {
      targetIds = targetIds.concat(
        instigatorParent.children()
          .map(c => { return c.id; })
          .filter(id => { return id !== instigatorTag.instance.id; })
      );
    } else if (instigatorIsRnode) {
      //if we're an rnode, only move things that aren't our own endpoint
      targetIds = targetIds.concat(
        this.canvas.store.topNodes()
          .map(n => { return n.id; })
          .filter(id => {
            const link = instigatorTag.link();
            return id !== instigatorTag.instance.getTopLevelParent().id
            && link.fromNode() && id !== link.fromNode().getTopLevelLinkParent().id
            && link.toNode() && id !== link.toNode().getTopLevelLinkParent().id;
          })
      );
    } else {
      // otherwise we can move top parents
      targetIds = targetIds.concat(
        this.canvas.store.topNodes()
          .map(n => { return n.id; })
          .filter(id => { return id !== instigatorTag.instance.getTopLevelParent().id; })
      );
    }

    targetIds.forEach(id => {
      const target = this.canvas.store.getNode(id);
      if (!target) {
        return;
      }
      if (target.link()) {
        return;
      }
      if (collectedData.nodeMoves[target.id] && collectedData.nodeMoves[target.id].count > 25) {
        console.log("relenting attempt to fix overlaps after too many moves");
        return;
      }
      const targetRect = this.getRect(target.id);
      if (!targetRect) {
        return;
      }
      if (target.getHidden()) {
        return;
      }
      if (instigatorTag.instance.id == target.id) {
        return;
      }

      if (!zoomUI.initialized) {
        console.log("bailing fixing overlaps for zoom not initialized");
        return;
      }

      const scale = this.canvas.getMatrix().scale;

      if (this.hit(rect, targetRect, this.gutterSize, scale)) {
        const { direction, move, targetTag } = this.getSeparatingMove({ instigator: instigatorTag.instance, target, prevDirection, allowedDirections });
        if (!targetTag) {
          return;
        }

        const prevNodePosition = this.canvas.store.getNodePositions([target.id])[0];

        targetTag.instance.applyMove({ dx: move.dx, dy: move.dy, appliedMoveLookup: {} });
        targetTag.updateNodesDisplayRecursively();

        if (!collectedData.nodeMoves[target.id]) {
          collectedData.nodeMoves[target.id] = { count: 1, move };
          collectedData.prevNodePositions[target.id] = prevNodePosition;
        } else {
          collectedData.nodeMoves[target.id].count++;
          collectedData.nodeMoves[target.id].move.dx += move.dx;
          collectedData.nodeMoves[target.id].move.dy += move.dy;
        }

        // keep our rectangles up to date
        this.calculateTagRect(targetTag);
        if (this.getFirstFreehandAncestorId(targetTag)) {
          const topParentId = targetTag.instance.getTopLevelParent().id;
          const topParentTag = this.canvas.nodeTags[topParentId];
          this.calculateFreehandDescendantRects(topParentTag);
        }

        this.fixOverlapsRecursively({ instigatorTag: targetTag, prevDirection: direction, collectedData, allowedDirections });
      }
    });

    // we didn't get a hit or a move.  If we're a child of a freehand
    // container, we may have more freehand ancestors who need adjustments.
    if (isFreehandChild && instigatorParent) {
      const instigatorParentTag = this.canvas.nodeTags[instigatorParent.id];
      if (!this.checkTag(instigatorParentTag)) {
        return;
      }

      const firstFreehandAncestorId = this.getFirstFreehandAncestorId(instigatorParentTag);

      // yup, we do.
      if (firstFreehandAncestorId) {
        const firstFreehandAncestor = this.canvas.store.getNode(firstFreehandAncestorId);
        if (!firstFreehandAncestor) {
          return;
        }

        const ancestorIds = instigatorTag.instance.ancestors().map(a => { return `${a.id}`; });
        const children = firstFreehandAncestor.children();
        const childrenIds = children.map(c => { return `${c.id}`; });
        const newInstigatorId = childrenIds.find(childId => { return ancestorIds.includes(`${childId}`); });
        if (!newInstigatorId) {
          return;
        }
        const newInstigatorTag = this.canvas.nodeTags[newInstigatorId];
        this.fixOverlapsRecursively({ instigatorTag: newInstigatorTag, prevDirection, collectedData, allowedDirections });

      } else {
        // nope, we don't.  Make sure our top parent doesn't have any overlaps
        // after we've been mucking inside.
        const topParent = instigatorTag.instance.getTopLevelParent();
        if (!topParent) {
          return;
        }
        const topParentTag = this.canvas.nodeTags[topParent.id];
        this.fixOverlapsRecursively({ instigatorTag: topParentTag, prevDirection, collectedData, allowedDirections });
      }
    }

  }

  getInstigatorTag(nodeTag) {
    const topParent = nodeTag.instance.getTopLevelParent();
    const topParentTag = this.canvas.nodeTags[topParent.id];
    const firstFreehandAncestorId = this.getFirstFreehandAncestorId(nodeTag);

    // if we don't have a freehand ancestor only the top parent can instigate moves
    if (!firstFreehandAncestorId) {
      return topParentTag;
    }

    // otherwise our top ancestor who is a child of our first freehand ancestor instigates.
    // freehand, any freehand ancestor, or the real top parent may instigate.
    const ancestors = nodeTag.instance.ancestors();
    const idx = ancestors.findIndex(a => { return a.id == firstFreehandAncestorId; });
    let instigatorTag;
    if (idx === 0) {
      instigatorTag = nodeTag;
    } else {
      instigatorTag = this.canvas.nodeTags[ancestors[idx - 1].id];
    }
    return instigatorTag;
  }

  getSeparatingMove({ instigator, target, prevDirection, allowedDirections }) {
    if (!(instigator && target)) {
      throw new Error('instigator and target are required');
    }

    const instigatorRect = this.getRect(instigator.id);
    const targetRect = this.getRect(target.id);
    if (!(instigatorRect && targetRect)) {
      return;
    }

    const targetTag = this.canvas.nodeTags[target.id];
    if (!targetTag) {
      return;
    }

    const scale = this.canvas.getMatrix().scale;
    const gutterSize = this.gutterSize;

    let moves = [
      [ 'right', (gutterSize - (targetRect.left - instigatorRect.right) / scale)],
      [ 'left', (gutterSize - (instigatorRect.left - targetRect.right) / scale)],
      [ 'down', (gutterSize - (targetRect.top - instigatorRect.bottom) / scale)],
      [ 'up', (gutterSize - (instigatorRect.top - targetRect.bottom) / scale)]
    ];

    if (allowedDirections && allowedDirections.length) {
      moves = moves.filter(m => allowedDirections.includes(m[0]));
    }

    const move = prevDirection
      ? moves.find(m => { return m[0] == prevDirection; })
      : moves.sort((a, b) => { return a[1] - b[1]; })[0];

    switch(move[0]) {
      case 'right': {
        return { direction: 'right', move: { dx: move[1], dy: 0 }, targetTag };
      }
      case 'left': {
        return { direction: 'left', move: { dx: -move[1], dy: 0 }, targetTag };
      }
      case 'down': {
        return { direction: 'down', move: { dx: 0, dy: move[1] }, targetTag };
      }
      case 'up': {
        return { direction: 'up', move: { dx: 0, dy: -move[1] }, targetTag };
      }
      default:
        throw new Error('bad move');
    }
  }


  unhighlightAllCardBorders() {
    $('.highlight-card-borders').removeClass('highlight-card-borders');
  }

  highlightCardBorders(node) {
    appUI.highlightCardBorders(node);
  }

  unhighlightCardBorders(node) {
    appUI.unhighlightCardBorders(node);
  }

  shiftRect(rect, dx, dy) {
    rect.top += dy;
    rect.y = rect.top;
    rect.bottom += dy;
    rect.height = rect.bottom - rect.top;
    rect.left += dx;
    rect.right += dx;
    rect.x = rect.left;
    rect.width = rect.right - rect.left;
  }

  getLNodePointForAdoptedNode({ link, draggingRect }) {
    const linkTag = this.canvas.linkTags[link.id];

    const { circleCenter, radius, thirdPoint } = linkTag.describeArc();
    const rect = scaleRect({ rect:draggingRect, canvas: this.canvas });
    const nodeCenter = {
      x: rect.left + (rect.width / 2),
      y: rect.top + 17,
    };

    if (link.lineCurvature === 'elbow') {
      return nodeCenter;
    } else {

      const angleCircleCenterToNodeCenter = normalizeAngle(computeSlope(circleCenter, nodeCenter));

      const point = {
        x: circleCenter.x + (radius * Math.cos(angleCircleCenterToNodeCenter)),
        y: circleCenter.y + (radius * Math.sin(angleCircleCenterToNodeCenter)),
      };

      return point;
    }
  }

  getSharedParentIds({ nodeIds }) {
    const topLevelParentIds = [];

    for (const nodeId of nodeIds) {
      const node = this.canvas.store.getNode(nodeId);
      const parent = node.getTopLevelParent();
      if (!topLevelParentIds.includes(parent.id)) {
        topLevelParentIds.push(parent.id);
      }
    }
    return topLevelParentIds;
  }

  bulkRealignLinks(nodeTags) {
    const topLevelParentNodeTags = nodeTags.map(nt => nt && nt.topLevelParentTag());
    const done = {};
    for (const topLevelParentNodeTag of topLevelParentNodeTags) {
      if (topLevelParentNodeTag && !done[topLevelParentNodeTags.id]) {
        topLevelParentNodeTag.rediscoverLinkPositions();
      }
    }
  }

  fitCardsIntoFreehand({ nodeTags }) {
    if (!nodeTags.length) return;
    const allInFreehand = nodeTags.every(e => e.instance.getGoverningLayoutName() === 'layout-freehand');
    if (!allInFreehand) return;

    const { canvas } = this;
    const escapedNodeTags = nodeTags.filter(({ instance: { x, y } }) => x < FREEHAND_PADDING || y < FREEHAND_PADDING);

    for (const nodeTag of escapedNodeTags) {
      const { instance } = nodeTag;
      const { x, y } = instance;

      if (x < FREEHAND_PADDING) {
        instance.applyMove({
          dx: FREEHAND_PADDING - x,
          dy: 0,
          appliedMoveLookup: {},
          canvas,
        });
      }

      if (y < FREEHAND_PADDING) {
        instance.applyMove({
          dx: 0,
          dy: FREEHAND_PADDING - y,
          appliedMoveLookup: {},
          canvas,
        });
      }

      nodeTag.updatePosition();
    }
  }

  setTransparent({ nodeIds }) {
    for (const id of nodeIds) {
      const nodeTag = this.canvas.getNodeTag({ id });
      if (nodeTag) {
        nodeTag.setTransparent();
      }
    }
  }

  unsetTransparent({ nodeIds }) {
    if (!nodeIds) return;
    for (const id of nodeIds) {
      const nodeTag = this.canvas.getNodeTag({ id });
      if (nodeTag) {
        nodeTag.unsetTransparent();
      }
    }
  }
  reclipLink(draggingRect, dragNodeTag) {

    const linkTags = dragdrop.getHoveredLinks({
      draggingNodeId: dragNodeTag.id,
      draggingRect,
      buffer: 0
    });

    if (this && this.clippedLinkTags[dragNodeTag.id]) {
      for (const linkTag of this.clippedLinkTags[dragNodeTag.id]) {
        linkTag.update();
      }
      this.clippedLinkTags[dragNodeTag.id].length = 0;
    }

    if (linkTags.length) {
      this.clippedLinkTags[dragNodeTag.id] = this.clippedLinkTags[dragNodeTag.id] || [];
      for (const linkTag of linkTags) {
        this.clippedLinkTags[dragNodeTag.id].push(linkTag);
        linkTag.setClipPolygon();
      }
    }
  }
}


// singleton instance
const nui = new NodesUI();

export { NodesUI, nui };



// WEBPACK FOOTER //
// ./starling/src/lib/nodes-ui.js